import type { MotorData } from "../calculationTypes";
import { computed } from "vue";
import {
  getMotorData,
  getMotorRerateVoltage,
  getTotalNameplateVoltageForFrequencyV,
  get50HzNameplate,
  getMotorCurrentA,
  getMotorElevationDerateFactor
} from "@/libs/calculations/motors";
import { getMultiphaseTotalDynamicHeadFt } from "@/libs/calculations/pumps";
import { shaft_adjusted_hp_load } from "@/libs/calculations/pumps/toleranceData";
import { DEFAULT_HPS_MOTOR_TEMPERATURE_F } from "@/libs/calculations/constants";
import type { MotorCtx } from "./context";

// Motor loading, nameplate/rerate values and electrical calcs.
export const defineMotor = (ctx: MotorCtx) => {
  const {
    _equipment,
    _wellProperties,
    _parameters,
    pumpHz,
    isScenarioEspShore,
    descendingOrderPumps,
    primaryPump,
    bottomPump,
    basicData,
    fluidPropertiesAtMotor,
    fluidPropertiesAtPumpIntake,
    fluidPropertiesAfterPumpDischargeTubingContext,
    pumpIntakePressurePSIA,
    downholeMotorTemperatureF,
    basePumpData,
    pumpData,
    totalRequiredBrakeHP
  } = ctx;

  const motorPoles = computed<number>(() => {
    return _equipment.value?.r_motor?.poles || 2;
  });

  const adjustedPumpHzFrequency = computed<number>(() => {
    return pumpHz.value * (motorPoles.value / 2);
  });

  const baseDischargeFluidDensityLBMFT3 = computed(() => {
    if (!primaryPump.value) {
      return (
        fluidPropertiesAfterPumpDischargeTubingContext.value?.fluidProperties
          .fluidDensityLBMFT3 || 0
      );
    }

    return (
      basePumpData.value?.individualPumpCalculations?.[primaryPump.value?.id]
        ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
        .fluidProperties?.fluidDensityLBMFT3 || 0
    );
  });

  const baseProducedDischargePressurePSIA = computed(() => {
    if (primaryPump.value) {
      return (
        basePumpData.value.individualPumpCalculations[primaryPump.value?.id]
          ?.dischargeStageCalculations?.dischargePressurePSIA || 0
      );
    }

    return pumpIntakePressurePSIA.value;
  });

  const baseProducedTotalDynamicHeadFt = computed(() => {
    return getMultiphaseTotalDynamicHeadFt(
      baseProducedDischargePressurePSIA.value - pumpIntakePressurePSIA.value,
      baseDischargeFluidDensityLBMFT3.value
    );
  });

  const baseAverageViscosityCP = computed(
    () =>
      (fluidPropertiesAtPumpIntake.value.fluidViscosityCP +
        (basePumpData.value?.individualPumpCalculations?.[
          descendingOrderPumps.value?.[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties?.fluidViscosityCP || 0)) /
      2
  );

  const baseRequiredPumpBrakeHP = computed(
    () => basePumpData.value.requiredPumpBrakeHP
  );

  const baseRequiredSealBrakeHP = computed(
    () => basePumpData.value.requiredSealBrakeHP
  );

  const baseRequiredIntakeBrakeHP = computed(
    () => basePumpData.value.requiredIntakeBrakeHP
  );

  const baseTotalRequiredBrakeHP = computed(
    () =>
      (baseRequiredPumpBrakeHP.value || 0) +
      (baseRequiredSealBrakeHP.value || 0) +
      (baseRequiredIntakeBrakeHP.value || 0)
  );

  const baseTotalRequired60HzBrakeHP = computed(
    () => baseTotalRequiredBrakeHP.value * (60 / _parameters.value?.pump_hz)
  );

  // Based from 60 Hz
  const legacyMotorLoadRatio = computed(
    () =>
      baseTotalRequired60HzBrakeHP.value /
        (hpElevationDerated.value * _parameters.value?.motor_rerate_ratio) || 0
  );

  const motorData = computed<MotorData>(() => {
    // Calculate Motor Data
    return getMotorData(
      basicData.value,
      fluidPropertiesAtMotor.value,
      _equipment.value?.r_motor,
      primaryPump.value,
      basePumpData.value.individualPumpCalculations[primaryPump.value?.id]
        ?.efficiencyRatio,
      _parameters.value?.pump_hz,
      baseAverageViscosityCP.value,
      _equipment.value?.r_shroud?.interior_diameter_inches,
      _wellProperties.value?.r_top_casing,
      _wellProperties.value?.top_casing_length_feet,
      _wellProperties.value?.r_bottom_casing,
      legacyMotorLoadRatio.value,
      reratedNameplateHP.value,
      reratedNameplateV.value,
      amperageNameplate.value,
      _parameters.value?.motor_rerate_ratio,
      baseProducedTotalDynamicHeadFt.value,
      baseTotalRequired60HzBrakeHP.value,
      basePumpData.value.individualPumpCalculations[primaryPump.value?.id]
        ?.intakeStageCalculations?.intakeFluidPropertiesContext.fluidProperties
        .fluidDensityLBMFT3 || 0,
      basePumpData.value.individualPumpCalculations[bottomPump.value?.id]
        ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
        .fluidProperties.fluidDensityLBMFT3 || 0,
      downholeMotorTemperatureF.value
    );
  });

  // motorLoadRatioRR (rerated) from motorData
  const motorLoadRatio = computed(() => motorData.value.motorLoadRatioRR);

  // non rereated motor load ratio (used for motor eff and motor power factor)
  const motorLoadRatioNP = computed(() => motorData.value.motorLoadRatioNP);

  // voltage corrections computed from motorData (uses motorLoadRatioNP internally)
  const motorVoltageCorrections = computed(
    () => motorData.value.motorVoltageCorrections
  );

  const rpmCorrectionForPumpCalculations = computed(() => {
    if (!_equipment.value?.r_motor) {
      return 1;
    }

    const correction = motorData.value.motorVoltageCorrections?.rpmCorrection;
    return Number.isFinite(correction) && correction > 0 ? correction : 1;
  });

  // pumpData accounts for seal/intake bhp
  const operatingRPM = computed(() => {
    return pumpData.value.operatingRPM;
  });

  const motorInputW = computed(() => {
    return (
      motorData.value.motor_voltage *
      motorData.value.motor_current *
      motorData.value.motor_power_factor *
      Math.sqrt(3)
    );
  });

  const motorOutputW = computed(() => {
    return motorInputW.value * motorData.value.motor_efficiency;
  });

  const motorShaftPowerLimitHP = computed(() =>
    shaft_adjusted_hp_load(
      _equipment.value?.r_motor?.shaft_horsepower,
      operatingRPM.value
    )
  );

  const motorShaftRatingRatio = computed(
    () => totalRequiredBrakeHP.value / motorShaftPowerLimitHP.value
  );

  const motorEnergyKWH = computed(
    () =>
      (totalRequiredBrakeHP.value / motorData.value.motor_efficiency) * 0.7457
  );

  const hpNameplate = computed(
    () => _equipment.value?.r_motor?.nameplate_horsepower || 0
  );

  // For HPS scenarios, apply altitude derate before rerate ratio
  const hpElevationDerated = computed(() => {
    if (isScenarioEspShore.value) return hpNameplate.value;
    return (
      hpNameplate.value *
      getMotorElevationDerateFactor(_parameters.value?.elevation_feet_asl)
    );
  });

  const voltageNameplate = computed(
    () => _equipment.value?.r_motor?.nameplate_voltage || 0
  );

  const amperageNameplate = computed(
    () => _equipment.value?.r_motor?.nameplate_current || 0
  );

  const reratedNameplateHP = computed(
    () => hpElevationDerated.value * _parameters.value?.motor_rerate_ratio
  );

  const nameplate50HzHP = computed(() =>
    get50HzNameplate(_equipment.value?.r_motor?.nameplate_horsepower)
  );

  const reratedNameplateV = computed(() => {
    return (
      getMotorRerateVoltage(
        voltageNameplate.value,
        _parameters.value?.motor_rerate_ratio,
        _equipment.value?.r_motor?.rr_vb,
        _equipment.value?.r_motor?.rr_v1,
        _equipment.value?.r_motor?.rr_v2,
        _equipment.value?.r_motor?.rr_v3
      ) || 0
    );
  });

  const nameplate50HzV = computed(() =>
    get50HzNameplate(_equipment.value?.r_motor?.nameplate_voltage)
  );

  const reratedNameplateA = computed(
    () =>
      getMotorCurrentA(
        _parameters.value?.motor_rerate_ratio,
        amperageNameplate.value,
        _equipment.value?.r_motor?.ib,
        _equipment.value?.r_motor?.i1,
        _equipment.value?.r_motor?.i2,
        _equipment.value?.r_motor?.i3,
        _equipment.value?.r_motor?.i4,
        _equipment.value?.r_motor?.i5
      ) || 0
  );

  // Some motors amps do not exactly line up with their ratings so
  // this will mask that for the users whenever motor isn't rerated
  const adjustedReratedNameplateA = computed(() => {
    if (_parameters.value?.motor_rerate_ratio === 1) {
      return amperageNameplate.value;
    } else {
      return reratedNameplateA.value;
    }
  });

  // Some motors volts do not exactly line up with their ratings so
  // this will mask that for the users whenever motor isn't rerated
  const adjustedReratedNameplateV = computed(() => {
    if (_parameters.value?.motor_rerate_ratio === 1) {
      return voltageNameplate.value;
    } else {
      return reratedNameplateV.value;
    }
  });

  // Some motors hp do not exactly line up with their ratings so
  // this will mask that for the users whenever motor isn't rerated
  const adjustedReratedNameplateHP = computed(() => {
    if (_parameters.value?.motor_rerate_ratio === 1) {
      return hpNameplate.value;
    } else {
      return reratedNameplateHP.value;
    }
  });

  const motorVoltageV = computed(() =>
    getTotalNameplateVoltageForFrequencyV(
      reratedNameplateV.value,
      _parameters.value?.pump_hz
    )
  );

  const motorVoltage60HzV = computed(() =>
    getTotalNameplateVoltageForFrequencyV(reratedNameplateV.value, 60)
  );

  const flowVelocityAroundMotorFTSEC = computed(
    () => fluidPropertiesAtMotor.value.liquidSuperficialVelocityFTSEC
  );

  const motorCurrentA = computed(() => motorData.value.motor_current);

  const motorInternalTemperatureF = computed(
    () => motorData.value.internal_temperature
  );

  const motorSkinTemperatureF = computed(() => {
    if (!isScenarioEspShore.value) {
      return DEFAULT_HPS_MOTOR_TEMPERATURE_F;
    }
    return motorData.value.skin_temperature;
  });

  const motorEfficiencyRatio = computed(() => motorData.value.motor_efficiency);

  const motorPowerFactor = computed(() => motorData.value.motor_power_factor);

  const motorRatedRPM = computed(() => motorData.value.motorRatedRPM);

  const bestMotorInternalTempF = computed(
    () =>
      motorData.value.internal_temperature ||
      motorData.value.skin_temperature ||
      150
  );

  return {
    motorPoles,
    adjustedPumpHzFrequency,
    baseDischargeFluidDensityLBMFT3,
    baseProducedDischargePressurePSIA,
    baseProducedTotalDynamicHeadFt,
    baseAverageViscosityCP,
    baseRequiredPumpBrakeHP,
    baseRequiredSealBrakeHP,
    baseRequiredIntakeBrakeHP,
    baseTotalRequiredBrakeHP,
    baseTotalRequired60HzBrakeHP,
    legacyMotorLoadRatio,
    motorData,
    motorLoadRatio,
    motorLoadRatioNP,
    motorVoltageCorrections,
    rpmCorrectionForPumpCalculations,
    operatingRPM,
    motorInputW,
    motorOutputW,
    motorShaftPowerLimitHP,
    motorShaftRatingRatio,
    motorEnergyKWH,
    hpNameplate,
    hpElevationDerated,
    voltageNameplate,
    amperageNameplate,
    reratedNameplateHP,
    nameplate50HzHP,
    reratedNameplateV,
    nameplate50HzV,
    reratedNameplateA,
    adjustedReratedNameplateA,
    adjustedReratedNameplateV,
    adjustedReratedNameplateHP,
    motorVoltageV,
    motorVoltage60HzV,
    flowVelocityAroundMotorFTSEC,
    motorCurrentA,
    motorInternalTemperatureF,
    motorSkinTemperatureF,
    motorEfficiencyRatio,
    motorPowerFactor,
    motorRatedRPM,
    bestMotorInternalTempF
  };
};

export type MotorCalcs = ReturnType<typeof defineMotor>;
